<template>
	<div class="w-full h-full overflow-y-auto visible-scrollbars">
		<dl class="session-detail-list">
			<dt>Date</dt>
			<dd class="-mt-2">
				<BaseTooltip :text="createdAt + ' in your timezone'" placement="bottom">
					{{ createdAtCreatorTime || createdAt }}
				</BaseTooltip>
			</dd>
			<SessionDetailsReporter />
			<template v-if="session.startUrl || session.endUrl">
				<dt>URL</dt>
				<dd>
					<a
						:href="session.startUrl || session.endUrl"
						target="_blank"
						rel="noopener noreferrer"
						class="break-words hover:text-gray-100"
					>
						{{ session.startUrl || session.endUrl }}
					</a>
					<button class="text-gray-400 hover:text-gray-100" title="Copy URL" @click="onCopyLinkClick">
						<BaseIcon v-if="!isCopyLinkButtonClicked" class="ml-4 mb-2" name="mono/clipboard" />
						<BaseIcon v-else class="ml-4 mb-2 text-green" name="mono/check-circle" />
					</button>
				</dd>
			</template>
			<template v-else>
				<dt>Domain</dt>
				<dd>{{ session.hostname }}</dd>
			</template>
			<!-- ONLY SHOWN TO WORKSPACE MEMBERS  -->
			<template v-if="$store.isUserWorkspaceMember">
				<template v-if="showAssigneeSection">
					<dt>Assigned to</dt>
					<dd class="-ml-2">
						<SessionManageAssignees />
					</dd>
				</template>

				<template
					v-if="
						showSessionLabelSection ||
						($workspaceSubscription?.isFreemiumPlan && !$store.hasUserProductViewerRole) ||
						isLabelsFeatureEnabled
					"
				>
					<dt>Labels</dt>
					<dd class="-ml-2">
						<SessionManageLabels />
					</dd>
				</template>

				<template v-if="!$store.isExtension">
					<dt class="flex items-center gap-4">
						<span>Integrations</span>
						<ButtonQuestionMark @click="toggleIntegrationsFeatureBanner" />
						<BaseTooltip
							v-if="!$store.hasUserBugReporterRole && !$store.hasUserProductViewerRole"
							text="Integration Settings"
							class="ml-auto"
						>
							<button class="ml-auto" @click="$routerHelpers.navigateToWorkspaceSettings('integrations')">
								<BaseIcon name="mono/settings" />
							</button>
						</BaseTooltip>
					</dt>
					<BannerSidebar
						v-if="isIntegrationsFeatureBannerVisible"
						learnMoreUrl="https://docs.birdeatsbug.com/latest/integrations/automation.html"
						class="mb-8"
						@close="toggleIntegrationsFeatureBanner"
					>
						<div class="flex gap-8 items-center h-28">
							<BaseIcon name="jira" class="w-16 h-16" />
							<BaseIcon name="linear" class="w-16 h-16" />
							<BaseIcon name="azure" class="w-16 h-16" />
							<BaseIcon name="github" class="w-16 h-16 text-gray-100" />
							<BaseIcon name="slack" class="w-16 h-16" />
						</div>
						<span class="text-gray-300">
							Auto send recordings to Slack, create jira issues and more.
							<template v-if="!isIntegrationsFeatureEnabled">Available in team plan.</template>
						</span>
					</BannerSidebar>
					<dd>
						<div v-if="showIntegrationSection" class="flex flex-wrap gap-8 mb-8 overflow-hidden">
							<SessionDetailsIntegrationLink
								v-for="(integration, index) in session.integrations"
								:key="integration.url + index"
								:integration="integration"
								:removeable="canUserEditSession"
							/>
						</div>
						<SessionDetailsIntegrationShareButtons
							v-if="canUserEditSession && isIntegrationsFeatureEnabled"
							:session="session"
						/>
					</dd>
				</template>
			</template>
		</dl>
	</div>
</template>

<script>
import {storeToRefs} from 'pinia'
import SessionManageLabels from 'shared/components/SessionManageLabels.vue'
import SessionDetailsIntegrationLink from 'shared/components/SessionDetailsIntegrationLink.vue'
import SessionDetailsIntegrationShareButtons from 'shared/components/SessionDetailsIntegrationShareButtons.vue'
import SessionDetailsReporter from 'shared/components/SessionDetailsReporter.vue'
import SessionManageAssignees from './SessionManageAssignees.vue'
import ButtonQuestionMark from 'shared/components/ButtonQuestionMark.vue'
import BannerSidebar from 'shared/components/BannerSidebar.vue'
import copyToClipboard from 'shared/utils/copyToClipboard'
import {useSessionStore} from 'shared/stores/session'
import {FEATURE_CODES} from 'shared/constants/featureCodes'

export default {
	name: 'SessionDetails',
	components: {
		SessionManageLabels,
		SessionDetailsIntegrationLink,
		SessionDetailsIntegrationShareButtons,
		SessionManageAssignees,
		ButtonQuestionMark,
		BannerSidebar,
		SessionDetailsReporter,
	},
	setup() {
		const sessionStore = useSessionStore()
		const {session, canUserEditSession} = storeToRefs(sessionStore)
		return {session, canUserEditSession}
	},
	data() {
		return {
			isCopyLinkButtonClicked: false,
			copyLinkClickTimer: null,
			isIntegrationsFeatureBannerVisible:
				localStorage?.isIntegrationsFeatureBannerVisible !== 'false' && !this.session.integrations?.length,
		}
	},
	computed: {
		createdAt() {
			const startedAt = new Date(this.session.startedAt)
			const time = startedAt.toLocaleTimeString('en-GB', {hour12: true})
			const timeWithoutSec = time.replace(/(\d?\d:\d\d):\d\d\s?(\w\w)/, '$1$2')
			const dateString = startedAt.toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'short',
				year: '2-digit',
			})
			return `${dateString}, ${timeWithoutSec}`
		},
		createdAtCreatorTime() {
			if (!this.session.timezone) return
			const startedAt = new Date(this.session.startedAt)
			const time = startedAt.toLocaleTimeString('en-GB', {hour12: true, timeZone: this.session.timezone})
			const timeWithoutSec = time.replace(/(\d?\d:\d\d):\d\d\s?(\w\w)/, '$1$2')
			const dateString = startedAt.toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'short',
				year: '2-digit',
				timeZone: this.session.timezone,
			})
			return `${dateString}, ${timeWithoutSec} ${this.session.timezone}`
		},
		showAssigneeSection() {
			return this.session.assignees?.length || this.canUserEditSession
		},
		showSessionLabelSection() {
			return this.$store.isSessionLabelEnabled && (this.session.labels?.length || this.canUserEditSession)
		},
		showIntegrationSection() {
			return this.session.integrations?.length
		},
		isIntegrationsFeatureEnabled() {
			return this.$store.hasPlanFeature(FEATURE_CODES.TOOL_INTEGRATIONS)
		},
		isLabelsFeatureEnabled() {
			return this.$store.hasPlanFeature(FEATURE_CODES.INCLUDE_LABELS)
		},
	},
	beforeUnmount() {
		clearTimeout(this.copyLinkClickTimer)
	},
	methods: {
		toggleIntegrationsFeatureBanner() {
			this.isIntegrationsFeatureBannerVisible = !this.isIntegrationsFeatureBannerVisible
			if (localStorage) {
				localStorage.isIntegrationsFeatureBannerVisible = String(this.isIntegrationsFeatureBannerVisible)
			}
		},
		onCopyLinkClick: function () {
			this.isCopyLinkButtonClicked = true
			this.copyLinkClickTimer = setTimeout(() => {
				this.isCopyLinkButtonClicked = false
				this.copyLinkClickTimer = null
			}, 2000)
			copyToClipboard(this.session.startUrl || this.session.endUrl)
		},
	},
}
</script>

<style>
.session-detail-list > dt {
	font-weight: 500;
	color: var(--gray-400);
	margin-bottom: 6px;
}

.session-detail-list > dd {
	color: var(--gray-300);
	margin-bottom: 20px;
}
</style>
